<template>
<div v-loading="loading" :element-loading-text="loadingText">
  <div class="condition-set-box mr-b25 advanced-input">
    <ul>
      <li><span class="stress">대상장비</span>{{objLabel}}</li>
      <li><span class="stress">자원</span>{{rscLabel}}</li>
      <li><span class="stress">예측기간</span>
        <el-input-number ref="predictPeriodField" style="width:95px;" size="mini" v-model="predictPeriod" controls-position="right" :min="1" :max="maxPredictPeriodDays" @change="setDataBasePredictPeriod" />
        <span>일</span>
        <span class="note">
          *{{recommendLearnDayCount}}일 이상의 학습 데이터 설정을 권장합니다.
        </span>
      </li>
      <li>
        <div class="stress">
          <span class="mr-r2">학습데이터</span>
          <el-tooltip effect="dark" placement="bottom" popper-class="performance-predict-popper">
            <span slot="content">
              예측 기간이 30일 이하인 경우 학습 기간은 월간 데이터 보관 기간 ({{ !isEmpty(dataStoragePeriodBasicinfo) ? dataStoragePeriodBasicinfo.month : '' }}개월) <br />
              이내에서 선택할 수 있으며, 예측 기간이 31일 이상인 경우 학습 기간은<br />
              연간 데이터 보관 기간 ({{ !isEmpty(dataStoragePeriodBasicinfo) ? dataStoragePeriodBasicinfo.year : '' }}년)
              이내에서 선택할 수 있습니다.
            </span>
            <i class="fas fa-info-circle c-pointer"></i>
          </el-tooltip>
        </div>
        <el-date-picker size="small" class="smart-picker" style="width:180px;" v-model="userDefineDates" type="daterange" :start-placeholder="defaultDateRangeOptions.startPlaceholder" :end-placeholder="defaultDateRangeOptions.endPlaceholder"
          :ranage-separator="defaultDateRangeOptions.rangeSeparator" :format="defaultDateRangeOptions.format" :picker-options="pickerOptions" />
        <el-button type="content" class="smart-btn" :disabled="isEmpty(userDefineDates)" @click="addLearnData">추가</el-button>
        <el-button type="content" class="smart-btn arrow" @click="expandedLearnDataSet = !expandedLearnDataSet"><i :class="expandedLearnDataSet ? 'fas fa-sort-down' : 'fas fa-sort-up'" /></el-button>
        <el-button type="primary" class="smart-primary" @click="onPredictPerfButtonClick">성능 예측</el-button>
      </li>
    </ul>
    <hr v-show="!isEmpty(learnDataSet) && expandedLearnDataSet" class="mr-b10" />
    <el-tag v-show="expandedLearnDataSet" class="mr-r10" v-for="(item, index) in learnDataSet" :key="index" :class="item.duplicate ? 'highlighted' : 'smart-tag'" closable :disable-transitions="true" @close="removeLearnData(index)">
      {{item.label}}
    </el-tag>
  </div>
  <div v-if="!isEmpty(predictData)" >
    <div class="vertical-center-box mr-b20">
      <span class="title-h4 mr-r10">예측 결과</span>
      <span class="text-em text-small">({{predictPeriodLabel}})</span>
    </div>
    <highcharts style="width:100%;" :options="chartOptions" />
  </div>
</div>
</template>

<script>
import { isNil, isEmpty, cloneDeep } from 'lodash';
import moment from 'moment';
import { getValueAfterCheckEmpty } from '@/utils/string';
import { removeTime, defaultDateRangeOptions } from '@/utils/date';
import msgUtil from '@/utils/message';
import predictPerfUtil from '@/utils/predict-performance';
import { lineOptions } from '@/constants/chart-options';
import SmartAnalysisApi from '@/api/common/smart-analysis';

export default {
  name: 'WPerformancePredictComponent',
  props: {
    rscInfo: {
      type: Object,
    },
  },
  data() {
    return {
      maxPredictPeriodDays: predictPerfUtil.MAX_PREIDCT_PERIOD_DAYS,
      loading: false,
      loadingText: '성능 예측 중입니다. 다소 시간이 소요될 수 있습니다.',
      objLabel: null,
      rscLabel: null,
      /* 성능 예측 조건 */
      dataStoragePeriodBasicinfo: null, // 데이터 보관 주기 객체
      predictPeriod: 1,
      predictPeriodUnit: predictPerfUtil.predictPeriodUnitDefine.DAY,
      learnDataSet: [],
      minLearnDate: null, // 최소 설정 가능한 날짜. (예측일자 - 1일 기준으로 계산)
      maxLearnDate: null, // 최대 설정 가능한 날짜. (예측일자 - 1일)
      maxNumberOfLearnDay: null, // 최대 학습 일수
      recommendLearnDayCount: null, // 권장 학습 일수
      defaultDateRangeOptions,
      pickerOptions: null,
      userDefineDates: [],
      expandedLearnDataSet: true,
      /* 예측 결과 */
      predictTaskName: 'predictPerfTask',
      predictPeriodLabel: null,
      chartOptions: null,
      predictData: null,
    };
  },
  watch: {
    rscInfo: {
      immediate: true,
      handler(value) {
        if (isEmpty(value)) {
          this.reset();
        } else {
          this.initialize();
        }
      },
    },
    predictData: {
      handler(value) {
        if (isEmpty(value)) {
          this.predictPeriodLabel = null;
        } else {
          const format = 'YY.MM.DD HH:mm';
          const startDatetime = moment(this.predictData[0][0]).format(format);
          const endDatetime = moment(this.predictData[this.predictData.length - 1][0]).format(format);
          this.predictPeriodLabel = `${startDatetime} ~ ${endDatetime}`;
        }
      },
    },
  },
  mounted() {
    this.initChartOptions();
  },
  beforeDestory() {
    this.$taskManager.removeTask(this.predictTaskName);
  },
  methods: {
    isEmpty,
    /**
     * 컴포넌트 초기 설정을 한다.
     * @author Ju
     */
    initialize() {
      this.objLabel = isEmpty(this.rscInfo.ipaddress) ? getValueAfterCheckEmpty(this.rscInfo.objName) : `${getValueAfterCheckEmpty(this.rscInfo.objName)} (${getValueAfterCheckEmpty(this.rscInfo.ipaddress)})`;
      this.rscLabel = `${getValueAfterCheckEmpty(this.rscInfo.managetypeName)} > ${getValueAfterCheckEmpty(this.rscInfo.rsctypeName)} > ${getValueAfterCheckEmpty(this.rscInfo.rscName)} (${getValueAfterCheckEmpty(this.rscInfo.rscKeyName)})`;
      // 데이터 보관 주기 조회
      predictPerfUtil
        .getDataStoragePeriodBasicinfo()
        .then((result) => {
          if (result.success) {
            this.dataStoragePeriodBasicinfo = result.data;
          } else {
            this.dataStoragePeriodBasicinfo = result.data;
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '데이터 보관 주기 정보', false));
          }
        })
        .catch((result) => {
          this.dataStoragePeriodBasicinfo = result.data;
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '데이터 보관 주기 정보', false));
        })
        .finally(() => {
          this.pickerOptions = {
            disabledDate: (date) => this.isUnselectableLearnDate(date),
          };
          this.setDataBasePredictPeriod(this.predictPeriod);
        });
    },
    /**
     * 차트 설정을 초기화한다.
     * @author Ju
     */
    initChartOptions() {
      const options = cloneDeep(lineOptions);
      options.chart.height = 300;
      options.xAxis.labels.format = '{value: %Y-%m-%d %H:%M}';
      this.chartOptions = options;
    },
    /**
     * 컴포넌트를 초기화한다.
     * @author Ju
     */
    reset() {
      this.loading = false;
      this.objLabel = null;
      this.rscLabel = null;

      this.predictPeriod = 1;
      this.learnDataSet = [];
      this.minLearnData = null;
      this.maxLearnData = null;
      this.maxNumberOfLearnDay = null;
      this.recommendLearnDayCount = null;
      this.pickerOptions = null;
      this.userDefineDates = [];
      this.expandedLearnDataSet = true;

      this.predictData = null;
      this.initChartOptions();
      this.$taskManager.removeTask(this.predictTaskName);
    },
    /**
     * 해당 날짜가 최초/최대 학습 날짜 기준으로 선택 가능한 날짜인지 판단한다.
     * @author Ju
     * @param  {Date}  date
     * @return {Boolean} true: 선택불가(비활성화) / false: 선택가능(활성화)
     */
    isUnselectableLearnDate(date) {
      return this.minLearnDate > date || this.maxLearnDate < date;
    },
    /**
     * 변경한 예측 기간에 맞게 데이터를 설정한다.
     * @author Ju
     */
    setDataBasePredictPeriod() {
      if (isNil(this.predictPeriod) || this.predictPeriod === '') {
        // 값이 없는 경우 강제로 최소 값이 설정되도록 한다. 'change' 이벤트가 발신 되는 시점과 currentValue가 설정되는 시점 문제로 nextTick에서 처리
        this.$nextTick(() => {
          this.$refs.predictPeriodField.setCurrentValue(1);
        });
        return;
      }
      // 최대 학습 일수, 권장 학습 일 수 등 설정
      this.maxNumberOfLearnDay = predictPerfUtil.getMaxLearnDayCount(this.predictPeriod, this.predictPeriodUnit);
      this.recommendLearnDayCount = predictPerfUtil.getRecommendLearnDayCount(this.predictPeriod, this.predictPeriodUnit);

      // 최소 학습 날짜, 최대 학습 날짜
      this.maxLearnDate = removeTime(new Date());
      this.minLearnDate = predictPerfUtil.getMinDateByPredictPeriod(this.dataStoragePeriodBasicinfo, this.maxLearnDate, this.predictPeriod, this.predictPeriodUnit);

      // 학습 데이터 초기화
      const endDatetime = new Date();
      let startDatetime = moment(endDatetime)
        .subtract(this.recommendLearnDayCount - 1, 'd')
        .toDate();
      if (startDatetime < this.minLearnDate) {
        startDatetime = this.minLearnDate;
      }
      this.learnDataSet = predictPerfUtil.addLearnData([], startDatetime, endDatetime);
    },
    /**
     * 학습 데이터를 추가한다.
     * @author Ju
     */
    addLearnData() {
      const startDatetime = this.userDefineDates[0];
      const endDatetime = this.userDefineDates[1];
      const duplicateIndex = predictPerfUtil.findOverlapLearnData(this.learnDataSet, startDatetime, endDatetime);
      if (duplicateIndex < 0) {
        if (predictPerfUtil.isExceedLearnDayCount(this.learnDataSet, startDatetime, endDatetime, this.maxNumberOfLearnDay)) {
          this.$message(msgUtil.getCustomMsgData(`학습 데이터는 최대 ${this.maxNumberOfLearnDay}일까지만 추가할 수 있습니다.`));
        } else {
          this.learnDataSet = predictPerfUtil.addLearnData(this.learnDataSet, startDatetime, endDatetime);
        }
      } else {
        this.$message(msgUtil.getCustomMsgData('설정한 기간을 포함하는 학습 기간이 추가되어있습니다.'));
      }
    },
    /**
     * 인덱스에 해당하는 학습 데이터를 삭제한다.
     * @author Ju
     * @param  {[type]} index [description]
     * @return {[type]}       [description]
     */
    removeLearnData(index) {
      this.learnDataSet.splice(index, 1);
      this.learnDataSet = predictPerfUtil.clearDuplicate(this.learnDataSet);
    },
    /**
     * 성능 예측 버튼 클릭 이벤트 핸들러
     * @author Ju
     */
    onPredictPerfButtonClick() {
      if (isEmpty(this.learnDataSet)) {
        this.$message(msgUtil.getCustomMsgData('학습 데이터를 설정해주십시오.'));
      } else {
        const totalLearnDayCount = predictPerfUtil.getTotalLearnDayCount(this.learnDataSet);
        if (totalLearnDayCount < this.recommendLearnDayCount) {
          this.$confirm(...msgUtil.getCustomConfirmData(`${this.recommendLearnDayCount}일 미만의 학습 데이터로 예측할 경우 정확도가 떨어질 수 있습니다. 예측을 계속 진행하시겠습니까?`)).then(() => {
            this.predictPerformance();
          }).catch(() => {});
        } else {
          this.predictPerformance();
        }
      }
    },
    /**
     * 성능 정보를 예측한다.
     * @author Ju
     */
    predictPerformance() {
      const learnDateArray = predictPerfUtil.convertLearnDataSetToDateStringArray(this.learnDataSet, this.rscInfo.registDatetime);
      const hashkeyIdString = this.rscInfo.hashkeyIdString;
      const predictParams = {
        trainDataList: learnDateArray,
        predictDateTimePeriod: predictPerfUtil.convertToDays(this.predictPeriod, this.predictPeriodUnit),
      };

      const me = this;
      this.predictData = null;
      this.loading = true;
      SmartAnalysisApi.predictPerformance({
        hashkeyIdString,
        predictParams: JSON.stringify(predictParams),
      })
        .then((response) => {
          if (response.data.success) {
            const predictRequestId = response.data.data;
            this.$taskManager.createTask(
              this.predictTaskName,
              1,
              () => {
                SmartAnalysisApi.checkCompletePredictPerformance(predictRequestId).then((result) => {
                  if (result.data.data) {
                    me.$taskManager.removeTask(this.predictTaskName);
                    this.loadPerformancePredictResult(predictRequestId);
                  }
                });
              },
              false,
              60,
              () => {
                me.$taskManager.removeTask(this.predictTaskName);
                this.loading = false;
                this.$message(msgUtil.getResultMsgData('예측', '성능 정보', false));
              },
            );
            this.$taskManager.startTask(this.predictTaskName);
          } else {
            this.$message(msgUtil.getResultMsgData('예측', '성능 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData('예측', '성능 정보', false));
          this.loading = false;
          throw new Error(e);
        });
    },
    /**
     * 성능 예측 결과를 조회한다.
     * @author Ju
     * @param  {[type]} predictRequestId [description]
     * @return {[type]}                  [description]
     */
    loadPerformancePredictResult(predictRequestId) {
      SmartAnalysisApi.getPerformancePredictReuslt(predictRequestId).then((response) => {
        if (!response.data.success || isEmpty(response.data.data)) {
          // 예측 데이터가 빈 배열인 경우 예측 실패한 경우이기 때문에 실패 메시지를 제공한다.
          this.$message(msgUtil.getResultMsgData('예측', '성능 정보', false));
        } else {
          this.predictData = response.data.data;
          this.chartOptions.series = [
            {
              data: this.predictData,
              name: '예측값',
              rscInfo: this.rscInfo,
            },
          ];
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData('예측', '성능 정보', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style lang="scss">
.perf_predict_box {
  border: 1px solid #e0e0e0;
  background-color: #f4f5f6;
  padding: 10px 15px;

  & > .el-row {
    margin: 4px 0;
    .label {
      width: 90px;
      font-size: 10pt;
      font-weight: 500;
    }
    .content {
      font-size: 10pt;
    }
  }
}
.performance-predict-popper.el-tooltip__popper {
  max-width: 600px;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/ResourceSmartAnalyzerPopup/components/PerformancePredictComponent.vue